import { ChevronRightIcon } from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CompanyData,
  useDeleteCompany,
  useGetCompany,
} from "src/api/Companies/companies";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";

export default function CompanySettings() {
  const { companyId } = useParams();
  const { data: company } = useGetCompany(String(companyId));
  return (
    <div className="flex h-full flex-col">
      <div className="h-12 shrink-0 items-center justify-between space-y-2 border-b border-gray-200/[.65] px-8 sm:flex sm:space-y-0">
        <div className="flex items-center space-x-2 text-sm">
          <Link
            className="text-gray-800 transition-all hover:text-gray-800"
            to="/companies"
          >
            Companies
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <Link
            className="flex items-center space-x-1.5 text-gray-800 transition-all hover:text-gray-800"
            to={`/companies/${companyId}`}
          >
            {company?.source === "hubspot" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="3 3.5 12 12"
                className="w-4"
              >
                {" "}
                <path
                  fill="#FF5C35"
                  d="M11.54 7.422V6.096a1.02 1.02 0 0 0 .588-.92v-.03c0-.56-.46-1.02-1.02-1.02h-.03c-.561 0-1.02.46-1.02 1.02v.03c0 .407.243.757.589.92v1.326c-.516.08-.986.292-1.375.605l-3.64-2.832a1.14 1.14 0 0 0 .04-.286 1.15 1.15 0 1 0-1.15 1.148c.206 0 .398-.059.566-.154l3.58 2.786a2.892 2.892 0 0 0-.482 1.603c0 .62.196 1.194.527 1.666l-1.089 1.09a.935.935 0 0 0-.27-.045.945.945 0 1 0 .945.945.934.934 0 0 0-.044-.27l1.077-1.077a2.89 2.89 0 0 0 1.761.598 2.905 2.905 0 0 0 .446-5.776Zm-.447 4.36a1.49 1.49 0 1 1 0-2.98 1.49 1.49 0 0 1 0 2.98Z"
                />
              </svg>
            )}
            <div>{company?.name}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Settings"}</div>
        </div>
      </div>
      <div className="w-full flex-1 space-y-16 overflow-auto px-8 py-[88px]">
        <div className="mx-auto max-w-2xl space-y-2">
          <label className="w-40 shrink-0 text-lg font-medium leading-none text-gray-800">
            Actions
          </label>
          <div className="space-y-6 divide-y rounded-md border p-8 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <label className="leading-none text-gray-600">Delete</label>
                <p className="text-sm font-light text-gray-500">
                  Deleting a company is a permanent action
                </p>
              </div>
              <div>
                {!!company && <DeleteCompanyButton company={company} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteCompanyButton({ company }: { company: CompanyData }) {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const deleteCompanyMutation = useDeleteCompany();

  useEffect(() => {
    if (deleteCompanyMutation.isSuccess) {
      setIsConfirmingDelete(false);
    }
  }, [deleteCompanyMutation.isSuccess]);

  return (
    <>
      <Button
        variant={"destructive"}
        onClick={() => {
          setIsConfirmingDelete(true);
        }}
        size={"sm"}
      >
        Delete
      </Button>
      <Dialog open={isConfirmingDelete} onOpenChange={setIsConfirmingDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Company Deletion</DialogTitle>
          </DialogHeader>

          <div className="my-4">
            <div className="text-sm">
              <span className="font-light text-gray-600">{`Are you sure you want to delete the company `}</span>
              <span className="font-medium">{`${company?.name}`}</span>
              <span className="font-light text-gray-600">{`?`}</span>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              onClick={() => {
                setIsConfirmingDelete(false);
              }}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              disabled={deleteCompanyMutation.isLoading}
              onClick={() => {
                deleteCompanyMutation.mutate({
                  _id: String(company._id),
                });
              }}
              variant={"destructive"}
            >
              {deleteCompanyMutation.isLoading ? "Deleting.." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
